import useTranslation from 'next-translate/useTranslation';
import { memo, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { Box, FormControl, Line, Modal, ModalProps, Typography } from '@core/component';
import { Translate } from '@core/constant';
import { useProductAlert, useValidator } from '@core/hook';
import {
  ETypographyVariant,
  UpdateProductAlertForm,
  getUpdateProductAlertSchema,
} from '@core/type';
import { ProductAlertUpdateProps } from '@core/type/api';

import {
  ButtonsModalUpdateAlert,
  HeaderModal,
  LineProductAlertField,
  ModalSuccessProductAlertUpdate,
} from './components';
import { ModalUpdateProductAlertProps } from './interface-modal-alert';

const ModalUpdateProductAlertContainer = ({
  productAlert,
  isOpen,
  onCloseModal,
}: Omit<ModalProps, 'children'> & ModalUpdateProductAlertProps) => {
  const { updateProductAlert, removeFromProductAlerts } = useProductAlert();
  const { t: tProduct } = useTranslation(Translate.common.PRODUCT);
  const { t: tForm } = useTranslation(Translate.common.FORM);

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitSuccessful },
  } = useForm<UpdateProductAlertForm>({
    defaultValues: {
      amount: productAlert.totalPrice,
    },
    resolver: useValidator(
      getUpdateProductAlertSchema(productAlert?.product?.totalPrice),
      () => null,
    ),
    mode: 'onChange',
  });

  const handleFormCb = (data: UpdateProductAlertForm) => {
    const submitData: ProductAlertUpdateProps = {
      amount: data.amount,
      id: productAlert.id,
    };
    reset();

    return updateProductAlert(submitData);
  };

  const deleteAlert = () => {
    removeFromProductAlerts(productAlert.id);
    onCloseModal();
    reset();
  };

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  return (
    <>
      <Modal isOpen={isOpen && !isSubmitSuccessful} onCloseModal={onCloseModal}>
        <HeaderModal
          title={tProduct('modals.addPriceAlert.title')}
          subTitle={productAlert.product?.name}
        />
        <Modal.Body>
          <Box display="flex" marginBottom="32px">
            <Typography variant={ETypographyVariant.BODY2}>
              {tProduct('productAlerts.editAlertModal.alreadySet')}
            </Typography>
          </Box>
          <Line marginBottom="32px" />

          <FormControl
            t={tForm}
            trigger={trigger}
            reset={reset}
            handleSubmit={handleSubmit}
            setError={setError}
            fn={handleFormCb}
          >
            <LineProductAlertField
              control={control}
              product={productAlert.product}
              errors={errors}
              watch={watch}
            />

            <Box marginTop={'32px'}>
              <ButtonsModalUpdateAlert onDelete={deleteAlert} onCancel={onCloseModal} />
            </Box>
          </FormControl>
        </Modal.Body>
      </Modal>
      <ModalSuccessProductAlertUpdate
        name={productAlert.product?.name}
        isOpen={isOpen && isSubmitSuccessful}
        onCloseModal={onCloseModal}
      />
    </>
  );
};

export const ModalUpdateProductAlert = memo(ModalUpdateProductAlertContainer);
